/**
 * Totals building utilities
 *
 * IMPORTANT: System permits use MAX (shared permit), NOT SUM.
 * Production values use SUM across wells.
 *
 * The database view (ui_report_production_master_v4) now includes:
 * - sys_h_permit_af, sys_o_permit_af, sys_total_permit_af (MAX per system/year)
 * - sys_ytd_gal, sys_ytd_af (SUM per system/year)
 * - sys_remaining_allocation_af, sys_pct_permit (calculated from system values)
 *
 * These values are pre-calculated, so we can use them directly for system-level
 * aggregations without re-computing.
 */

import { safeAdd, isNotAggregated } from "../utils";

/**
 * Create an empty totals object with all fields initialized to 0
 * @returns {import("../types").Totals}
 */
export const createEmptyTotals = () => ({
  h_permit_af: 0,
  o_permit_af: 0,
  total_permit_af: 0,
  jan: 0,
  feb: 0,
  mar: 0,
  apr: 0,
  may: 0,
  jun: 0,
  jul: 0,
  aug: 0,
  sep: 0,
  oct: 0,
  nov: 0,
  dec: 0,
  ytd_gal: 0,
  ytd_af: 0,
  h_permit_value_gal: 0,
  o_permit_value_gal: 0,
});

/**
 * Accumulate a row's per-well values into a totals object.
 * This sums ALL fields including permits - use only when appropriate
 * (e.g., for well-level aggregation or when aggregating across different systems).
 *
 * @param {import("../types").Totals} totals - The totals object to update
 * @param {import("../types").ProductionRow} row - The row to accumulate
 * @returns {import("../types").Totals} The updated totals object
 */
export const accumulateRow = (totals, row) => {
  totals.h_permit_af = safeAdd(totals.h_permit_af, row.h_permit_af);
  totals.o_permit_af = safeAdd(totals.o_permit_af, row.o_permit_af);
  totals.total_permit_af = safeAdd(totals.total_permit_af, row.total_permit_af);
  totals.jan = safeAdd(totals.jan, row.jan);
  totals.feb = safeAdd(totals.feb, row.feb);
  totals.mar = safeAdd(totals.mar, row.mar);
  totals.apr = safeAdd(totals.apr, row.apr);
  totals.may = safeAdd(totals.may, row.may);
  totals.jun = safeAdd(totals.jun, row.jun);
  totals.jul = safeAdd(totals.jul, row.jul);
  totals.aug = safeAdd(totals.aug, row.aug);
  totals.sep = safeAdd(totals.sep, row.sep);
  totals.oct = safeAdd(totals.oct, row.oct);
  totals.nov = safeAdd(totals.nov, row.nov);
  totals.dec = safeAdd(totals.dec, row.dec);
  totals.ytd_gal = safeAdd(totals.ytd_gal, row.ytd_gal);
  totals.ytd_af = safeAdd(totals.ytd_af, row.ytd_af);
  totals.h_permit_value_gal = safeAdd(
    totals.h_permit_value_gal,
    row.h_permit_value_gal
  );
  totals.o_permit_value_gal = safeAdd(
    totals.o_permit_value_gal,
    row.o_permit_value_gal
  );
  return totals;
};

/**
 * Create a totals object populated with system-level values from a row.
 * Use this for aggregate system totals where permits should NOT be summed.
 *
 * @param {import("../types").ProductionRow} row - A row from the system (any row works)
 * @returns {import("../types").Totals} Totals with system-level values
 */
export const createTotalsFromSystemRow = (row) => ({
  // Use system-level values for permits (MAX)
  h_permit_af: Number(row.sys_h_permit_af) || 0,
  o_permit_af: Number(row.sys_o_permit_af) || 0,
  total_permit_af: Number(row.sys_total_permit_af) || 0,
  h_permit_value_gal: Number(row.sys_h_permit_value_gal) || 0,
  o_permit_value_gal: Number(row.sys_o_permit_value_gal) || 0,
  // Use system-level values for production (SUM across wells)
  jan: Number(row.sys_jan) || 0,
  feb: Number(row.sys_feb) || 0,
  mar: Number(row.sys_mar) || 0,
  apr: Number(row.sys_apr) || 0,
  may: Number(row.sys_may) || 0,
  jun: Number(row.sys_jun) || 0,
  jul: Number(row.sys_jul) || 0,
  aug: Number(row.sys_aug) || 0,
  sep: Number(row.sys_sep) || 0,
  oct: Number(row.sys_oct) || 0,
  nov: Number(row.sys_nov) || 0,
  dec: Number(row.sys_dec) || 0,
  ytd_gal: Number(row.sys_ytd_gal) || 0,
  ytd_af: Number(row.sys_ytd_af) || 0,
});

/**
 * Accumulate system-level values from a row into totals.
 * Use this when aggregating ACROSS different systems (e.g., aquifer totals, grand totals).
 * Only accumulates if we haven't seen this system+year combination before.
 *
 * @param {import("../types").Totals} totals - The totals object to update
 * @param {import("../types").ProductionRow} row - The row to accumulate
 * @param {Set<string>} seenSystems - Set of "agg_system_ndx|report_year" strings already processed
 * @returns {import("../types").Totals} The updated totals object
 */
export const accumulateSystemRow = (totals, row, seenSystems) => {
  const key = `${row.agg_system_ndx}|${row.report_year}`;

  // Only accumulate if we haven't seen this system+year combination
  if (!seenSystems.has(key)) {
    seenSystems.add(key);
    // Use system-level values
    totals.h_permit_af = safeAdd(totals.h_permit_af, row.sys_h_permit_af);
    totals.o_permit_af = safeAdd(totals.o_permit_af, row.sys_o_permit_af);
    totals.total_permit_af = safeAdd(
      totals.total_permit_af,
      row.sys_total_permit_af
    );
    totals.jan = safeAdd(totals.jan, row.sys_jan);
    totals.feb = safeAdd(totals.feb, row.sys_feb);
    totals.mar = safeAdd(totals.mar, row.sys_mar);
    totals.apr = safeAdd(totals.apr, row.sys_apr);
    totals.may = safeAdd(totals.may, row.sys_may);
    totals.jun = safeAdd(totals.jun, row.sys_jun);
    totals.jul = safeAdd(totals.jul, row.sys_jul);
    totals.aug = safeAdd(totals.aug, row.sys_aug);
    totals.sep = safeAdd(totals.sep, row.sys_sep);
    totals.oct = safeAdd(totals.oct, row.sys_oct);
    totals.nov = safeAdd(totals.nov, row.sys_nov);
    totals.dec = safeAdd(totals.dec, row.sys_dec);
    totals.ytd_gal = safeAdd(totals.ytd_gal, row.sys_ytd_gal);
    totals.ytd_af = safeAdd(totals.ytd_af, row.sys_ytd_af);
    totals.h_permit_value_gal = safeAdd(
      totals.h_permit_value_gal,
      row.sys_h_permit_value_gal
    );
    totals.o_permit_value_gal = safeAdd(
      totals.o_permit_value_gal,
      row.sys_o_permit_value_gal
    );
  }

  return totals;
};

/**
 * Accumulate production values from every row, but only accumulate system-level
 * permit values once per system+year. Use this for well-detail totals where
 * production should be summed across all wells but permits should not be stacked.
 *
 * Special handling for "Not Aggregated" wells: since they all share the same
 * agg_system_ndx but each has individual permits, we sum per-well permit values
 * instead of using system-level deduplication.
 *
 * @param {import("../types").Totals} totals - The totals object to update
 * @param {import("../types").ProductionRow} row - The row to accumulate
 * @param {Set<string>} seenSystems - Set of "agg_system_ndx|report_year" strings already processed
 * @returns {import("../types").Totals} The updated totals object
 */
export const accumulateWellDetailRow = (totals, row, seenSystems) => {
  // Always accumulate per-well production values (every well contributes)
  totals.jan = safeAdd(totals.jan, row.jan);
  totals.feb = safeAdd(totals.feb, row.feb);
  totals.mar = safeAdd(totals.mar, row.mar);
  totals.apr = safeAdd(totals.apr, row.apr);
  totals.may = safeAdd(totals.may, row.may);
  totals.jun = safeAdd(totals.jun, row.jun);
  totals.jul = safeAdd(totals.jul, row.jul);
  totals.aug = safeAdd(totals.aug, row.aug);
  totals.sep = safeAdd(totals.sep, row.sep);
  totals.oct = safeAdd(totals.oct, row.oct);
  totals.nov = safeAdd(totals.nov, row.nov);
  totals.dec = safeAdd(totals.dec, row.dec);
  totals.ytd_gal = safeAdd(totals.ytd_gal, row.ytd_gal);
  totals.ytd_af = safeAdd(totals.ytd_af, row.ytd_af);

  // Check if this is a "Not Aggregated" / "No Permit" well
  const isNotAgg = isNotAggregated(row.agg_system_name);

  if (isNotAgg) {
    // "Not Aggregated" wells each have individual permits - sum per-well values
    totals.h_permit_af = safeAdd(totals.h_permit_af, row.h_permit_af);
    totals.o_permit_af = safeAdd(totals.o_permit_af, row.o_permit_af);
    totals.total_permit_af = safeAdd(
      totals.total_permit_af,
      row.total_permit_af
    );
    totals.h_permit_value_gal = safeAdd(
      totals.h_permit_value_gal,
      row.h_permit_value_gal
    );
    totals.o_permit_value_gal = safeAdd(
      totals.o_permit_value_gal,
      row.o_permit_value_gal
    );
  } else {
    // Regular aggregate systems - only count permit once per system+year
    const key = `${row.agg_system_ndx}|${row.report_year}`;
    if (!seenSystems.has(key)) {
      seenSystems.add(key);
      totals.h_permit_af = safeAdd(totals.h_permit_af, row.sys_h_permit_af);
      totals.o_permit_af = safeAdd(totals.o_permit_af, row.sys_o_permit_af);
      totals.total_permit_af = safeAdd(
        totals.total_permit_af,
        row.sys_total_permit_af
      );
      totals.h_permit_value_gal = safeAdd(
        totals.h_permit_value_gal,
        row.sys_h_permit_value_gal
      );
      totals.o_permit_value_gal = safeAdd(
        totals.o_permit_value_gal,
        row.sys_o_permit_value_gal
      );
    }
  }

  return totals;
};
